import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/charts/styles.css';

import { MantineProvider } from '@mantine/core';
import { ModalsProvider } from '@mantine/modals';
import { Notifications } from '@mantine/notifications';
import { QueryClientProvider } from '@tanstack/react-query';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { I18nextProvider } from 'react-i18next';
import { RouterProvider } from 'react-router/dom';

import { queryClient } from '@/api/queryClient';
import { AuthProvider } from '@/auth/AuthProvider';
import i18n from '@/i18n';
import { DatesLocaleProvider } from '@/i18n/DatesLocaleProvider';
import { MantineLabelsProvider } from '@/i18n/MantineLabelsProvider';
import { router } from '@/router';
import { theme } from '@/theme';

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root is missing in index.html');

// Provider order: SPEC-FRONTEND §2. Note that `modals.open*()` content renders inside
// ModalsProvider — outside the query client, i18n provider and router — so imperative modals
// must stay plain; anything needing hooks/links belongs in a component-level <Modal>.
createRoot(container).render(
  <StrictMode>
    <MantineProvider theme={theme} defaultColorScheme="light">
      <MantineLabelsProvider>
        <DatesLocaleProvider>
          <ModalsProvider>
            <Notifications position="top-right" limit={4} />
            <QueryClientProvider client={queryClient}>
              <I18nextProvider i18n={i18n}>
                <AuthProvider>
                  <RouterProvider router={router} />
                </AuthProvider>
              </I18nextProvider>
            </QueryClientProvider>
          </ModalsProvider>
        </DatesLocaleProvider>
      </MantineLabelsProvider>
    </MantineProvider>
  </StrictMode>,
);
